'use client';

import { useProductSortConfig } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/ProductSortProvider';
import { getAttrFromProduct } from '@hyphengroup/cms-components/types/attrNames';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import { useBreakpoint } from 'car-insurance/hooks/useBreakpoint';
import { useMemo } from 'react';
import { TIOptions } from '../TIOptions/TIOptions';
import { TISearchFooter } from '../TISearchFooter';
import Translations from './i18n';
import { TISearchBanner } from './TISearchBanner';
import { TravelPolicySearchProps } from './types';
import { useTravelSearchPoll } from './useTravelSearchPoll';
import { useTravelSearchQuery } from './useTravelSearchQuery';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import {
  getProviderTripTypes,
  travelCompareQuotesParamsHash,
} from '$/(cms)/_modules/travelQuotesService/helpers';
import { TravelCompareQuotesParams } from '$/(cms)/_modules/travelQuotesService/types';
import { TI_QUOTE_STATUS_SLOT_ID } from '$/components/cms/components-internal/SortAndFilterPanel/quoteStatusSlot';
import { PortalTo } from '$/components/Portal';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

export default function TravelPolicySearch({
  metaConfig,
  ...travelPolicySearchProps
}: TravelPolicySearchProps) {
  const {
    pageData: { travelQuotes },
  } = usePageDataContext();
  // query params - is the main source of truth

  const { travelQueryParams, setTravelQueryParams, queryParamsReady } =
    useTravelSearchQuery(travelPolicySearchProps);

  useTravelSearchPoll({
    queryParamsReady,
    travelQueryParams,
  });

  // The panel owns the results column when it is on the page, so the footer
  // belongs inside it rather than above the row on its own.
  const panelGovernsPage = !!useProductSortConfig();

  const onChangeParams = async (newParams: TravelCompareQuotesParams) => {
    setTravelQueryParams(newParams);
  };

  const handleRetry = () => {
    setTravelQueryParams({ ...travelQueryParams });
  };

  const getVisibleCount = (isMobile: boolean) => {
    return (
      metaConfig?.product_card?.filter((pc) => {
        const matchesPremium = travelQuotes?.premiums?.some(
          (premium) =>
            premium?.brokerProductCode ===
            getAttrFromProduct('INSUREMO_PRODUCT_UID', pc.productBlock?.product)
              ?.value,
        );
        if (!matchesPremium || !pc.visible) return false;
        if (isMobile) return pc.visible_mobile !== false;
        return true;
      }).length ?? 0
    );
  };

  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'sm';

  const context = useClientContext();
  const staticText = getClientTranslations(Translations, context);

  const providerSettings = travelQuotes?.providerSettings;
  // providerName is set only on the Provider Page and absent on the Result Page (the
  // travel quotes page, which carries metaConfig). It is the single signal used to pick
  // between the two layouts: the Provider Page pins the trip-details panel and shows the
  // inline availability banner, while the Result Page shows the results footer.
  const providerName = providerSettings?.providerName;
  const isProviderPage = !!providerName;
  const tripTypes = useMemo(
    () => getProviderTripTypes(providerSettings),
    [providerSettings],
  );

  const inProgress = travelQuotes?.status === 'inprogress' || !queryParamsReady;
  const hasError =
    (travelQuotes?.status === 'fail' || travelQuotes?.status === 'timeout') &&
    queryParamsReady;
  // A provider with no products for either trip type only happens behind provider
  // settings (content creators see it while building a page before linking products).
  const showEmptyState = isProviderPage && tripTypes.length === 0;
  // The unavailable toggle option stays disabled, but the user can still land on it
  // via URL param — surface the inline message when that selected type is unavailable.
  const showNotAvailable =
    tripTypes.length > 0 &&
    !tripTypes.includes(travelQueryParams.insuranceType);
  let bannerMessage = '';
  if (hasError && !inProgress) {
    bannerMessage = tr('searchError', staticText);
  }
  if (showNotAvailable && providerName) {
    bannerMessage = tr('tripTypeNotAvailable', staticText, {
      provider_name: providerName,
    });
  }

  if (showEmptyState) {
    return <TISearchBanner message={staticText.noProducts} />;
  }

  // Test-hook contract (CMS-1878). `inProgress` alone has a window where it
  // reads false between the first paint and the poll effect starting, so
  // `data-settled` also requires the quotes on screen to belong to the current
  // filter — true only once there is nothing left to wait for.
  const quotesMatchFilter =
    travelCompareQuotesParamsHash(travelQuotes?.params ?? {}) ===
    travelCompareQuotesParamsHash(travelQueryParams);

  // The panel's results bar is the count once a panel governs the page, so the
  // footer reports only what nothing else does: progress, error and empty.
  const footer = (hideCount: boolean) => (
    <TISearchFooter
      className="mt-spacing/gap/y-sm"
      inProgress={inProgress}
      hasError={hasError}
      resultCount={getVisibleCount(isMobile)}
      onRetry={handleRetry}
      hideCount={hideCount}
    />
  );

  return (
    <div
      data-component="travel-policy-search"
      data-settled={!inProgress && quotesMatchFilter ? 'true' : 'false'}
    >
      <TIOptions
        searchParams={travelQueryParams}
        onChangeParams={onChangeParams}
        countries={travelQuotes?.countries ?? []}
        regions={travelQuotes?.regions ?? []}
        boundaries={travelPolicySearchProps}
        queryParamsReady={queryParamsReady}
        tripTypes={tripTypes}
        sticky
      />
      {!!bannerMessage && isProviderPage && (
        <TISearchBanner
          className="mt-spacing/gap/y-sm"
          dismissible
          message={bannerMessage}
        />
      )}
      {!isProviderPage && (
        <>
          {panelGovernsPage && (
            <PortalTo rootId={TI_QUOTE_STATUS_SLOT_ID}>{footer(true)}</PortalTo>
          )}
          {!panelGovernsPage && footer(false)}
        </>
      )}
    </div>
  );
}
